import React, { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import i18n from 'simple-react-i18n'
import useApplicationSetting, { floatParser, intParser, listStringParser } from 'utils/customHook/useApplicationSetting'
import { groupBy, isUndefined, keyBy, max, range, round, sumBy, minBy, keys, values, compact, omit } from 'lodash'
import { DEFAULT_REGROUPMENT, NITRATES_CODE } from 'catchment/constants/CatchmentConstant'
import moment from 'moment'
import { shallowEqual, useSelector } from 'react-redux'
import { REMARK_CODES, searchMaxAnalysis, searchMaxResult, searchP90Result } from 'utils/AnalyseUtils'
import { getPercentage } from 'utils/NumberUtil'
import { exportPictureIcon } from 'components/echart/EChartUtils'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import useStartPeriod from 'catchment/utils/useStartPeriod'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { THRESHOLD_COLOR_BY_SIZE } from 'utils/QualityThresholdUtils'
import { THRESHOLD } from 'quality/constants/QualityConstants'
import { getSettingBool } from 'utils/SettingUtils'
import { getDate } from 'utils/DateUtil'
import { blackColor, disabledBorderInputColor } from 'utils/constants/ColorTheme'

const DEFAULT_NITRATE_THRESHOLDS = [2, 10, 25, 50]
const DEFAULT_PESTICIDES_THRESHOLDS = [0.1, 2]
const DEFAULT_SUM_PESTICIDES_THRESHOLDS = [0.5, 5]

const legendFilterSeries = (selectedLegend, dataByDate, getName, thresholdsColors) => {
    const omitThresholds = thresholdsColors.map((color, i) => selectedLegend[getName(i)] === false ? `threshold${i}` : undefined)
    const noData = selectedLegend?.[i18n.noData] === false ? 'noData' : undefined
    return keys(dataByDate).reduce((acc, act) => {
        acc[act] = omit(dataByDate[act], compact([...omitThresholds, noData]))
        return acc
    }, {})
}

const EvolutionChart = ({
    title = '',
    dateList = [],
    datas = [],
    selectedLegend = {},
    setSelectedLegend = () => {},
}) => {
    const series = useMemo(() => (
        datas.map(({ name, dataList, color }) => {
            const formattedDatas = dataList.map(d => ({
                value: [d.date, d.value],
            }))
            return {
                type: 'bar',
                data: formattedDatas,
                name,
                // name: color, // p90 <= 2 mg/l
                barMaxWidth: 30,
                stack: true,
                itemStyle: {
                    color,
                },
                label: {
                    show: true,
                    formatter: obj => {
                        return round(obj.data.value[1], 2) || ''
                    },
                    position: 'inside',
                    color: blackColor,
                },
            }
        })
    ), [datas])

    const options = {
        title: {
            text: title,
            left: 'center',
            top: '5%',
        },
        series,
        xAxis: [{
            type: 'category',
            data: dateList.toReversed?.() ?? [...dateList].reverse(), // compatibility issue
            boundaryGap: true,
            showSplitLine: true,
            axisLabel: {
                rotate: 50,
            },
        }],
        yAxis: [{
            type: 'value',
            name: `${i18n.catchmentPercentage} (%)`,
            position: 'left',
            nameLocation: 'center',
            nameGap: 50,
            boundaryGap: true,
            min: 0,
            max: 100,
        }],
        legend: {
            top: '10%',
            left: '10%',
            right: '8%',
            type: 'scroll',
            selected: selectedLegend,
        },
        grid: {
            top: '20%',
            left: '6%',
            right: '3%',
            bottom: '20',
            containLabel: true,
            height: 300,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                snap: true,
            },
            formatter: params => {
                return params.map(({ marker, seriesName, value: [, value] }) => `${marker} ${seriesName}: ${round(value, 3)}%`).join('</br>')
            },
        },
        toolbox: {
            right: '3%',
            top: '10%',
            feature: {
                saveAsImage: {
                    title: i18n.export,
                    icon: exportPictureIcon,
                    name: i18n.evolutionContaminationNitratesPesticides,
                },
            },
        },
    }

    return (
        <ReactECharts
            echarts={echarts}
            option={options}
            notMerge
            lazyUpdate
            className={'row no-margin'}
            style={{ height: 400 }}
            onEvents={
                {
                    legendselectchanged: legend => setSelectedLegend(legend.selected),
                }
            }
        />
    )
}

EvolutionChart.propTypes = {
    title: PropTypes.string,
    dateList: PropTypes.arrayOf(PropTypes.string),
    datas: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        color: PropTypes.string,
        dataList: PropTypes.arrayOf(PropTypes.shape({
            date: PropTypes.string,
            value: PropTypes.number,
        })),
    })),
    selectedLegend: PropTypes.shape({}),
    setSelectedLegend: PropTypes.func,
}

const calcMinYear = (analysis) => {
    const minYear = values(analysis).map(lst => {
        return minBy(lst, 'analysisDate')
    })
    return getDate(minBy(minYear, 'analysisDate')?.analysisDate, 'YYYY')
}

const NitratesChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
}) => {
    const {
        settings,
        qualitometers,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.applicationSettings,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const [selectedLegend, setSelectedLegend] = useState({})

    const threshold1 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_1', floatParser)
    const threshold2 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_2', floatParser)
    const threshold3 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_3', floatParser)
    const threshold4 = useApplicationSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_4', floatParser)

    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)

    const groupedAnalysis = useMemo(() => {
        const nitratesAnalysis = analysis.filter(({ parameter }) => parameter === NITRATES_CODE).filter(a => a.remark === REMARK_CODES.VALID)
        return groupBy(nitratesAnalysis, 'qualitometer')
    }, [analysis])

    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const defaultStartPeriod = useStartPeriod(intParser)
    const startPeriod = useMemo(() => (firstCatchmentYear ? calcMinYear(groupedAnalysis) : defaultStartPeriod), [firstCatchmentYear, groupedAnalysis, defaultStartPeriod])
    const endPeriod = useEndPeriod(intParser)

    const rangeDate = useMemo(() =>(range(endPeriod, startPeriod - 1, -regroupment)), [endPeriod, startPeriod, regroupment])
    const formatDate = useCallback(date => (date === startPeriod || regroupment === 1 ? `${date}` : `${Math.max(startPeriod, date - regroupment + 1)}-${date}`), [regroupment, startPeriod])
    const dateList = useMemo(() => (rangeDate.map(formatDate)), [rangeDate, formatDate])

    const thresholds = useMemo(() => {
        const thresholdsSettings = [threshold1, threshold2, threshold3, threshold4].filter(t => !isUndefined(t))
        return thresholdsSettings.length ? thresholdsSettings : DEFAULT_NITRATE_THRESHOLDS
    },
    [threshold1, threshold2, threshold3, threshold4])
    const thresholdsColors = useMemo(() => (THRESHOLD_COLOR_BY_SIZE[thresholds.length + 1]), [thresholds.length])

    const dataByDate = useMemo(() => (
        rangeDate.reduce((acc, date) => {
            acc[date] = groupBy(catchments, c => {
                const startDate = moment(date - regroupment + 1, 'YYYY').startOf('year').valueOf()
                const endDate = moment(date, 'YYYY').endOf('year').valueOf()
                const points = catchmentPoints.filter(p => p.id === c.id)
                const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => !isUndefined(id))

                const validPeriodAnalysis = ids.flatMap(id => groupedAnalysis[id] || []).filter(a => a.analysisDate > startDate && a.analysisDate < endDate)
                if (validPeriodAnalysis.length === 0) {
                    return 'noData'
                }
                const result = useP90 ? searchP90Result(validPeriodAnalysis) : searchMaxResult(validPeriodAnalysis)
                const indice = thresholds.findIndex(t => result < t)
                return indice !== -1 ? `threshold${indice}` : `threshold${thresholds.length}`
            })
            return acc
        }, {})
    ), [rangeDate, catchments, regroupment, catchmentPoints, qualitometers, groupedAnalysis, useP90, thresholds])

    const getName = useCallback(i => {
        const data = useP90 ? 'P90' : 'MAX'
        if (i === 0) {
            return `${data} <= ${thresholds[i]} mg/l`
        }
        if (i === thresholds.length) {
            return `${data} > ${thresholds[i - 1]} mg/l`
        }
        return `${thresholds[i - 1]} < ${data} <= ${thresholds[i]} mg/l`
    }, [useP90, thresholds])

    const remainingDataByDate = useMemo(() => legendFilterSeries(selectedLegend, dataByDate, getName, thresholdsColors), [selectedLegend, dataByDate, getName, thresholdsColors])

    const datas = useMemo(() => ([
        ... thresholdsColors.map((color, i) => ({
            name: getName(i),
            color,
            dataList: rangeDate.map(date => {
                const value = remainingDataByDate[date][`threshold${i}`]?.length ?? 0
                return {
                    date: formatDate(date),
                    value: getPercentage(value, values(remainingDataByDate[date]).flat().length),
                }
            }),
        })),
        {
            name: i18n.noData,
            color: disabledBorderInputColor,
            dataList: rangeDate.map(date => {
                const value = remainingDataByDate[date].noData?.length ?? 0
                return {
                    date: formatDate(date),
                    value: getPercentage(value, values(remainingDataByDate[date]).flat().length),
                }
            }),
        },
    ]), [thresholdsColors, rangeDate, remainingDataByDate, formatDate, getName])

    return (
        <EvolutionChart
            title={i18n.distributionNitratesConcentration}
            dateList={dateList}
            datas={datas}
            selectedLegend={selectedLegend}
            setSelectedLegend={setSelectedLegend}
        />
    )
}

NitratesChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(CatchmentPointDto),
        PropTypes.shape({
            // CatchmentPointDto
            mainPoint: PropTypes.bool,
        }),
    ])),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
}

const PesticidesChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = {},
}) => {
    const {
        qualityThresholds,
        settings,
        qualitometers,
    } = useSelector(store => ({
        qualityThresholds: store.QualityReducer.qualityThresholds,
        settings: store.AdministrationReducer.applicationSettings,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const [selectedLegend, setSelectedLegend] = useState({})

    const threshold1 = useApplicationSetting('CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_1', floatParser)
    const threshold2 = useApplicationSetting('CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_2', floatParser)
    const sumThreshold1 = useApplicationSetting('CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_1', floatParser)
    const sumThreshold2 = useApplicationSetting('CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_2', floatParser)

    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)

    const groupedAnalysis = useMemo(() => {
        const pesticidesAnalysis = analysis.filter(({ parameter }) => parameter !== NITRATES_CODE)
        return groupBy(pesticidesAnalysis, 'qualitometer')
    }, [analysis])

    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const defaultStartPeriod = useStartPeriod(intParser)
    const startPeriod = useMemo(() => (firstCatchmentYear ? calcMinYear(groupedAnalysis) : defaultStartPeriod), [firstCatchmentYear, groupedAnalysis, defaultStartPeriod])
    const endPeriod = useEndPeriod(intParser)

    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')
    const threshold = useMemo(() => (qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC)), [qualityThresholds, specificThreshold])
    const thresholdsIndexed = useMemo(() => (keyBy(threshold?.thresholds ?? [], 'parameterCode')), [threshold])

    const rangeDate = useMemo(() =>(range(endPeriod, startPeriod - 1, -regroupment)), [endPeriod, startPeriod, regroupment])
    const formatDate = useCallback(date => (date === startPeriod || regroupment === 1 ? `${date}` : `${Math.max(startPeriod, date - regroupment + 1)}-${date}`), [regroupment, startPeriod])
    const dateList = useMemo(() => (rangeDate.map(formatDate)), [rangeDate, formatDate])

    const thresholds = useMemo(() => {
        const settingThresholds = [threshold1, threshold2].filter(t => !isUndefined(t))
        return settingThresholds.length ? settingThresholds : DEFAULT_PESTICIDES_THRESHOLDS
    }, [threshold1, threshold2])
    const thresholdsColors = useMemo(() => (THRESHOLD_COLOR_BY_SIZE[thresholds.length + 1]), [thresholds.length])

    const sumThresholds = useMemo(() => {
        const settingSumThresholds = [sumThreshold1 ?? threshold1, sumThreshold2 ?? threshold2].filter(t => !isUndefined(t))
        return settingSumThresholds.length ? settingSumThresholds : DEFAULT_SUM_PESTICIDES_THRESHOLDS
    }, [sumThreshold1, threshold1, sumThreshold2, threshold2])
    // const sumThresholdsColors = THRESHOLD_COLOR_BY_SIZE[sumThresholds.length + 1]

    const dataByDate = useMemo(() => (
        rangeDate.reduce((acc, date) => {
            const startDate = moment(date - regroupment + 1, 'YYYY').startOf('year').valueOf()
            const endDate = moment(date, 'YYYY').endOf('year').valueOf()
            acc[date] = groupBy(catchments, c => {
                const points = catchmentPoints.filter(p => p.id === c.id)
                const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => !isUndefined(id))

                const validPeriodAnalysis = ids.flatMap(id => groupedAnalysis[id] || []).filter(a => a.analysisDate > startDate && a.analysisDate < endDate)
                if (validPeriodAnalysis.length === 0) {
                    return 'noData'
                }
                const quantifiedAnalysis = validPeriodAnalysis.filter(a => a.remark === REMARK_CODES.VALID)
                if (quantifiedAnalysis.length === 0) return 'threshold0'

                const maxAnalysis = searchMaxAnalysis(quantifiedAnalysis)
                const paramThreshold = thresholdsIndexed[maxAnalysis.parameter]
                const thresholdsToTest = paramThreshold ? [paramThreshold.threshold1 ?? thresholds[0], paramThreshold.threshold2 ?? thresholds[1]].filter(t => !isUndefined(t)) : thresholds

                const maxResult = maxAnalysis.result
                const indiceResult = thresholdsToTest.findIndex(t => maxResult < t)

                const filteredAnalysis = listSumPesticides.length ? quantifiedAnalysis.filter(a => listSumPesticides.includes(a.parameter)) : quantifiedAnalysis
                const groupOperation = groupBy(filteredAnalysis, p => `${p.qualitometer}#${p.operation}`)
                const maxSum = max(keys(groupOperation).map(key => sumBy(groupOperation[key], 'result')))
                const indiceSum = sumThresholds.findIndex(t => maxSum < t)

                const indice = max([
                    indiceResult === -1 ? thresholdsToTest.length : indiceResult,
                    indiceSum === -1 ? sumThresholds.length : indiceSum,
                ])

                return indice !== -1 ? `threshold${indice}` : `threshold${thresholds.length}`
            })
            return acc
        }, {})
    ), [rangeDate, catchments, regroupment, catchmentPoints, qualitometers, groupedAnalysis, thresholds, listSumPesticides, sumThresholds, thresholdsIndexed])

    const getName = useCallback(i => {
        if (i === 0) {
            // return `${i18n.respectThreshold} ${thresholds[i]} μg/l (${i18n.maxByPesticides}) ${i18n.and} ${sumThresholds[i]} μg/l (${i18n.sum})`
            return `${i18n.respectThreshold} (${i18n.maxByPesticides}) ${i18n.and} ${sumThresholds[i]} μg/l (${i18n.sum})`
        }
        // return `${i18n.exceedingThreshold} ${thresholds[i - 1]} μg/l (${i18n.maxByPesticides}) ${i18n.and}/${i18n.or} ${sumThresholds[i - 1]} μg/l (${i18n.sum})`
        return `${i18n.exceedingThreshold} (${i18n.maxByPesticides}) ${i18n.and}/${i18n.or} ${sumThresholds[i - 1]} μg/l (${i18n.sum})`
    }, [sumThresholds])

    const remainingDataByDate = useMemo(() => legendFilterSeries(selectedLegend, dataByDate, getName, thresholdsColors), [selectedLegend, dataByDate, getName, thresholdsColors])

    const datas = useMemo(() => ([
        ... thresholdsColors.map((color, i) => ({
            name: getName(i),
            color,
            dataList: rangeDate.map(date => {
                const value = remainingDataByDate[date][`threshold${i}`]?.length ?? 0
                return {
                    date: formatDate(date),
                    value: getPercentage(value, values(remainingDataByDate[date]).flat().length),
                }
            }),
        })),
        {
            name: i18n.noData,
            color: disabledBorderInputColor,
            dataList: rangeDate.map(date => {
                const value = remainingDataByDate[date].noData?.length ?? 0
                return {
                    date: formatDate(date),
                    value: getPercentage(value, values(remainingDataByDate[date]).flat().length),
                }
            }),
        },
    ]), [thresholdsColors, rangeDate, remainingDataByDate, formatDate, getName])

    return (
        <EvolutionChart
            title={i18n.distributionRespectAEPThreshold}
            dateList={dateList}
            datas={datas}
            selectedLegend={selectedLegend}
            setSelectedLegend={setSelectedLegend}
        />
    )
}

PesticidesChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(CatchmentPointDto),
        PropTypes.shape({
            // CatchmentPointDto
            mainPoint: PropTypes.bool,
        }),
    ])),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
}

export { NitratesChart, PesticidesChart }